import { toggleLikeComment } from 'app/requests/comments';
import { useSelector, useDispatch } from 'react-redux';
import * as commonActions from 'app/actions/commons';
import { toastError } from 'app/utils/toastHelper';
import React, { useState, useMemo } from 'react';
import { setAvatarUser } from 'app/redux/global';
import { AppStoreState } from 'app/redux/store';
import { useTranslation } from 'next-i18next';
import { Like, Message } from 'types/chat';
import CommentEdit from './CommentEdit';
import CommentForm from './CommentForm';
import { User } from 'types/auth';

interface CommentProps {
  comment: Message;
  isReply?: boolean;
  onReply?: (commentId: number, replyText: string) => void;
  updateComments: (newComment: Message) => void;
  handleDelete: (commentId: number) => void;
}

const Comment: React.FC<CommentProps> = ({ comment, isReply, onReply, handleDelete, updateComments }) => {
  const { auth } = useSelector((state: AppStoreState) => state.global);
  const [showReplies, setShowReplies] = useState(false);
  const [isChange, setIsChange] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleLikeComment = async () => {
    dispatch(commonActions.setLoading(true));
    try {
      const newComment = await toggleLikeComment(comment?.id as number);
      updateComments(newComment);
    } catch (error) {
      toastError(error.message);
    } finally {
      dispatch(commonActions.setLoading(false));
    }
  }

  const handleReply = (replyText: string) => {
    if (!!onReply) {
      setIsChange(true);
      onReply(comment?.id as number, replyText);
    }
  }

  const isLikedByUser = useMemo(() => {
    return comment?.likes?.find((like: Like) => like.user_id == auth.id);
  }, [auth, comment])

  const replies = useMemo(() => {
    const reps = comment?.replies || [];
    if (isChange) {
      setShowReplies(reps.length > 0)
    }
    return reps;
  }, [comment?.replies, isChange]);

  return (
    <div
      className={`${isReply ? '' : 'card p-2'} mb-2`}
      style={{ borderRadius: 10 }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <img
          src={setAvatarUser(comment?.user as User)}
          className="rounded-circle mr-2 shadow-sm"
          width={43}
          height={43}
          alt={`${comment?.user?.name}'s avatar`}
          style={{ border: '1px solid #e9ecef' }}
        />
        <div className="flex-grow-1">
          <div className="p-">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-1" style={{ fontSize: '1rem', fontWeight: 600 }}>
                {comment?.user?.name}
                <small className="text-muted ml-2 text-size-xsm">
                  {comment?.created_at ? new Date(comment?.created_at).toLocaleString() : ''}
                </small>
              </h5>
            </div>
            {!isEdit &&
              <div className="d-flex mb-1" style={{ lineHeight: '1.2', color: '#333' }}>
                <span>{comment?.content}</span>
                {comment?.likes && comment?.likes?.length > 0 &&
                  <span className="ml-3 d-flex justify-content-between align-items-center ml-auto">
                    <span className="text-secondary pr-1">{comment?.likes?.length}</span>
                    <img width={18} alt="like" src="/images/commons/icons/like.png" />
                  </span>
                }
              </div>
            }

            {isEdit &&
              <CommentEdit
                updateComments={updateComments}
                onClose={() => setIsEdit(false)}
                comment={comment}
              />
            }
          </div>
          {!isEdit &&
            <div className="d-flex action mt-1">
              {auth?.id &&
                <span
                  className="text-size-xsm text-info cursor-pointer text-underline"
                  onClick={() => handleLikeComment()}
                >
                  {isLikedByUser ? t('actions.unlike') : t('actions.like')}
                </span>
              }
              {auth?.id == comment.user_id &&
                <span
                  className="ml-3 text-size-xsm text-info cursor-pointer text-underline"
                  onClick={() => setIsEdit(!isEdit)}
                >
                  {t('actions.edit')}
                </span>
              }
              {auth?.id == comment.user_id &&
                <span
                  className="ml-3 text-size-xsm text-info cursor-pointer text-underline"
                  onClick={() => handleDelete(comment?.id as number)}
                >
                  {t('actions.delete')}
                </span>
              }

              {auth?.id && !isReply && !replies?.length &&
                <span
                  className="ml-3 text-size-xsm text-info cursor-pointer text-underline"
                  onClick={() => setShowReplies(!showReplies)}
                >
                  {showReplies ? t('comments.hidden_replies') : t('comments.reply')}
                </span>
              }

              {replies?.length > 0 && (
                <span
                  className="ml-3 text-size-xsm text-info cursor-pointer text-underline"
                  onClick={() => setShowReplies(!showReplies)}
                >
                  {showReplies ? t('comments.hidden_replies') : t('comments.view_replies', { total: replies.length })}
                </span>
              )}
            </div>
          }

        </div>
      </div>
      {showReplies &&
        <div className="mt-3 pl-3" style={{ marginLeft: 30 }}>
          {auth?.id && !isReply && (
            <CommentForm
              onSubmit={(replyText: string) => handleReply(replyText)}
              isReply
            />
          )}
          <div className="mt-3">
            {replies.map((reply: Message) => (
              <Comment
                key={reply?.id}
                comment={reply}
                isReply
                updateComments={updateComments}
                handleDelete={() => handleDelete(reply?.id as number)}
              />
            ))}
          </div>
        </div>
      }
    </div>
  );
};

export default Comment;
